import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Clock, Loader2, Radio, XCircle } from 'lucide-react'
import { Drawer, adminMutate, apiGet, useToast } from '../admin/primitives.js'

/**
 * « SCANNER MAINTENANT » — progression puis résultat (§16-22).
 *
 * Deux principes tiennent tout l'écran :
 *
 * · ON NE MONTRE QUE CE QU'ON SAIT. La progression est « 2 / 5 sources
 *   vérifiées » parce que deux l'ont réellement été. Aucun pourcentage
 *   inventé, aucune barre qui avance toute seule pour faire patienter.
 * · AUCUN RÉSULTAT N'EST UN ÉCHEC. « Rien de nouveau » est une réponse,
 *   affichée comme telle : la veille a regardé, il n'y avait rien (§18).
 */

export interface ScanRun {
  id: string
  status: 'RUNNING' | 'OK' | 'PARTIAL' | 'FAILED'
  trigger: 'MANUAL' | 'SCHEDULED'
  started_at: string
  finished_at: string | null
  duration_ms: number | null
  sources_total: number | null
  sources_done: number
  queries_total: number | null
  queries_attempted: number
  events_found: number
  signals_created: number
  signals_updated: number
  errors: number
  reason: string | null
  sources: { label: string; state: string; detail: string | null }[]
}

const STATE_ICON: Record<string, ReactNode> = {
  OK: <CheckCircle2 size={13} className="vs-ok" aria-hidden="true" />,
  NOT_CONFIGURED: <Clock size={13} className="vs-warn" aria-hidden="true" />,
  STALE: <AlertTriangle size={13} className="vs-warn" aria-hidden="true" />,
  UNAVAILABLE: <XCircle size={13} className="vs-bad" aria-hidden="true" />,
  SCHEMA_ERROR: <AlertTriangle size={13} className="vs-warn" aria-hidden="true" />,
}
const STATE_LABEL: Record<string, string> = {
  OK: 'vérifiée', NOT_CONFIGURED: 'aucun collecteur autorisé',
  STALE: 'indisponible', UNAVAILABLE: 'injoignable',
  // §9 — le fournisseur répond, c'est la requête qui est en cause. Dire
  // « injoignable » enverrait chercher du côté du réseau ou du jeton.
  SCHEMA_ERROR: 'erreur de compatibilité API',
}

function dureeFr(ms: number | null): string {
  if (ms === null) return '—'
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} s`
}
function quandFr(iso: string): string {
  const delta = Date.now() - new Date(iso).getTime()
  if (delta < 60_000) return 'il y a quelques secondes'
  if (delta < 3600_000) return `il y a ${Math.floor(delta / 60_000)} min`
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
  })
}

/** Le récit du scan, en français, sans jargon d'implémentation. */
export function ScanResult({ run }: { run: ScanRun }) {
  const rien = run.events_found === 0 && run.signals_created === 0 && run.signals_updated === 0
  const partiel = run.status === 'PARTIAL' || run.errors > 0
    || run.sources.some((s) => s.state !== 'OK')

  return (
    <div className="vs-result">
      <h4 className="vs-result__title">
        {run.status === 'FAILED'
          ? 'SCAN INTERROMPU'
          : partiel ? 'SCAN TERMINÉ AVEC AVERTISSEMENTS' : 'SCAN TERMINÉ'}
      </h4>
      <p className="lc-hint">{quandFr(run.finished_at ?? run.started_at)} · {dureeFr(run.duration_ms)}</p>

      {run.reason ? <p className="vs-reason">{run.reason}</p> : null}

      <dl className="lc-kv vs-counts">
        <div><dt>Sources vérifiées</dt><dd>{run.sources_done}{run.sources_total !== null ? ` / ${run.sources_total}` : ''}</dd></div>
        <div><dt>Recherches exécutées</dt><dd>{run.queries_total ?? 0}</dd></div>
        <div><dt>Événements trouvés</dt><dd>{run.events_found}</dd></div>
        <div><dt>Nouveaux signaux</dt><dd>{run.signals_created}</dd></div>
        <div><dt>Signaux actualisés</dt><dd>{run.signals_updated}</dd></div>
        <div><dt>Erreurs</dt><dd>{run.errors}</dd></div>
      </dl>

      {rien && run.status !== 'FAILED' ? (
        /* §18 — ce n'est PAS une erreur, et le texte le dit franchement. */
        <p className="gd-calm vs-nothing">
          Aucun nouvel événement, aucun nouveau signal. La veille a regardé : il n'y avait rien de neuf.
        </p>
      ) : null}

      {run.sources.length > 0 ? (
        <>
          <h5 className="so-class-title">Détail par source</h5>
          <ul className="vs-sources">
            {run.sources.map((s, i) => (
              <li key={`${s.label}-${i}`} className="vs-source">
                {STATE_ICON[s.state] ?? STATE_ICON.OK}
                <span className="vs-source__label">{s.label}</span>
                <em className="lc-hint">{STATE_LABEL[s.state] ?? s.state}</em>
                {s.detail ? <span className="lc-hint vs-source__detail">{s.detail}</span> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}

/* ═════════════ LIGNE DÉDIÉE « INSTAGRAM STORIES » (§17, §40) ═══════════ */

interface StoriesView {
  enabled: boolean
  handle: string | null
  source_status: string | null
  last_collect_at: string | null
  stories: {
    id: string; external_id: string; published_at: string | null
    media_type: string | null; analyzed_at: string | null
    capture_available: boolean; vision_confidence: number | null
  }[]
}

/** Refus visant CE compte — jamais une panne du fournisseur. */
const REFUS_FR: Record<string, string> = {
  RATE_LIMITED: 'Le fournisseur a demandé de patienter — la collecte reprendra d’elle-même.',
  FORBIDDEN: 'Clé du fournisseur refusée — à renouveler dans les réglages.',
  ACCOUNT_NOT_FOUND: 'Ce compte est introuvable sur Instagram.',
  PRIVATE_OR_UNAVAILABLE: 'Ce compte est privé — ses Stories ne sont pas publiques.',
  UNAVAILABLE: 'Fournisseur momentanément indisponible.',
  SCHEMA_ERROR: 'Réponse du fournisseur inattendue.',
  DEGRADED: 'Certaines Stories n’ont pas pu être lues.',
}

/**
 * La collecte des Stories a SA ligne, et c'est délibéré.
 *
 * Elle a son propre fournisseur, son propre coût, son propre rythme — lire
 * une affiche prend des minutes là où une page web se récupère en une
 * seconde — et sa propre façon d'échouer. Fondue dans les compteurs du scan,
 * elle afficherait des zéros pour un travail en cours : c'est-à-dire mentir.
 *
 * Elle ne s'affiche QUE si la collecte est activée pour cet établissement ;
 * ailleurs, elle serait un reproche permanent.
 */
function StoriesLine({ venueId, dejaDansLeRapport }: {
  venueId: string
  /** Le compte-rendu du scan porte déjà sa ligne Stories : ne pas doubler. */
  dejaDansLeRapport: boolean
}) {
  const [v, setV] = useState<StoriesView | null | undefined>(undefined)

  const relire = useCallback(async () => {
    const d = await apiGet<StoriesView>(
      `/api/v1/market/social/venues/${venueId}/stories`).catch(() => null)
    setV(d)
  }, [venueId])

  useEffect(() => { void relire() }, [relire])

  if (v === undefined || v === null || !v.enabled) return null
  // Le rapport du scan porte les compteurs RÉELS de ce passage — Stories
  // vues, nouvelles, réutilisées, analysées. Afficher en plus cette ligne
  // vivante donnerait deux réponses à la même question.
  if (dejaDansLeRapport) return null

  const analysees = v.stories.filter((s) => s.analyzed_at !== null).length
  const refus = v.source_status ? REFUS_FR[v.source_status] ?? null : null
  const jamais = v.last_collect_at === null && !refus

  const etat = refus ?? (jamais
    ? 'Aucun relevé effectué pour l’instant.'
    : v.stories.length === 0
      // §39 — zéro Story n'est PAS un échec de collecte. Les deux situations
      // se ressemblent à l'écran et ne veulent pas dire la même chose.
      ? 'Aucune Story active lors du dernier relevé.'
      : `${v.stories.length} Story${v.stories.length > 1 ? 's' : ''} connue${v.stories.length > 1 ? 's' : ''}`
        + ` · ${analysees} analysée${analysees > 1 ? 's' : ''}`)

  return (
    <li className="vs-source" aria-live="polite">
      {refus ? STATE_ICON.STALE : jamais ? STATE_ICON.NOT_CONFIGURED : STATE_ICON.OK}
      <span className="vs-source__label">Instagram Stories</span>
      <em className="lc-hint">{refus ? 'reporté' : jamais ? 'en attente' : 'relevé'}</em>
      <span className="lc-hint vs-source__detail">{etat}</span>
    </li>
  )
}
/**
 * Tiroir de scan : lance la passe, suit sa progression, montre le résultat,
 * et garde les douze dernières passes sous la main (§22).
 */
export function VenueScanDrawer({ venueId, venueName, open, onClose, autoStart, onFinished }: {
  venueId: string | null
  venueName: string
  open: boolean
  onClose: () => void
  /** Ouvert depuis « Scanner maintenant » : on lance tout de suite. */
  autoStart?: boolean
  onFinished?: (() => void) | undefined
}) {
  const toast = useToast()
  const [run, setRun] = useState<ScanRun | null>(null)
  const [history, setHistory] = useState<ScanRun[]>([])
  const [starting, setStarting] = useState(false)
  const [blocked, setBlocked] = useState<string | null>(null)
  const started = useRef<string | null>(null)

  const charger = async (id: string) => {
    const d = await apiGet<{ runs: ScanRun[]; active: ScanRun | null }>(
      `/api/v1/soirees/market/venues/${id}/scan-runs`).catch(() => null)
    if (!d) return
    setHistory(d.runs)
    setRun(d.active ?? d.runs[0] ?? null)
  }

  const lancer = async (id: string) => {
    setStarting(true); setBlocked(null)
    const r = await adminMutate<{ ok?: boolean; run_id?: string; message?: string; error?: string }>(
      'POST', `/api/v1/soirees/market/venues/${id}/scan`, {})
    setStarting(false)
    if (r.status === 200 && r.data?.run_id) {
      await charger(id)
      return
    }
    // 409 = déjà en cours, 429 = cooldown ou budget. Dans les deux cas
    // l'utilisateur mérite la raison exacte, pas « échec » (§14, §15).
    setBlocked(r.data?.message ?? 'Scan impossible pour le moment.')
    await charger(id)
  }

  useEffect(() => {
    if (!open || !venueId) { started.current = null; setBlocked(null); return }
    void charger(venueId)
    if (autoStart && started.current !== venueId) {
      started.current = venueId
      void lancer(venueId)
    }
  }, [open, venueId, autoStart]) // eslint-disable-line react-hooks/exhaustive-deps

  // Suivi de la progression : on n'interroge QUE tant qu'une passe tourne.
  useEffect(() => {
    if (!open || !venueId || run?.status !== 'RUNNING') return
    const t = setInterval(() => {
      void apiGet<{ run: ScanRun }>(`/api/v1/soirees/market/scan-runs/${run.id}`)
        .then((d) => {
          setRun(d.run)
          if (d.run.status !== 'RUNNING') {
            void charger(venueId)
            onFinished?.()
            toast('ok', `Scan de « ${venueName} » terminé.`)
          }
        })
        .catch(() => undefined)
    }, 2000)
    return () => clearInterval(t)
  }, [open, venueId, run?.id, run?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const enCours = run?.status === 'RUNNING'

  return (
    <Drawer open={open} title={`Veille — ${venueName}`} onClose={onClose} footer={
      <div className="vw-foot">
        <div className="vw-foot__btns">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={onClose}>Fermer</button>
          <button type="button" className="lc-btn lc-btn--primary" disabled={starting || enCours}
            onClick={() => venueId && void lancer(venueId)}>
            {starting || enCours
              ? <><Loader2 size={14} className="vw-spin" /> {enCours ? 'Analyse en cours…' : 'Lancement…'}</>
              : <><Radio size={14} /> Scanner maintenant</>}
          </button>
        </div>
      </div>
    }>
      {blocked ? <p className="vw-dup" role="alert">{blocked}</p> : null}

      {enCours && run ? (
        <div className="vs-running" aria-live="polite">
          <span className="vs-pulse" aria-hidden="true" />
          <h4 className="vs-result__title">Analyse en cours…</h4>
          <p className="lc-hint">Démarrée {quandFr(run.started_at)}.</p>
          <dl className="lc-kv vs-counts">
            <div>
              <dt>Sources</dt>
              <dd>{run.sources_done} / {run.sources_total ?? '?'} vérifiées</dd>
            </div>
            <div>
              <dt>Recherches</dt>
              <dd>{run.queries_total ?? '?'} à exécuter</dd>
            </div>
          </dl>
          <p className="lc-hint">
            La collecte respecte un délai entre chaque requête — quelques dizaines de secondes sont normales.
          </p>
        </div>
      ) : run ? (
        <ScanResult run={run} />
      ) : (
        <p className="gd-calm">Aucune passe enregistrée pour cet établissement.</p>
      )}

      {/* §17 — la collecte de Stories a son propre rythme : elle survit à la
          fin de la passe et continue de rendre compte ici. */}
      {venueId ? (
        <ul className="vs-sources">
          <StoriesLine venueId={venueId}
            dejaDansLeRapport={(run?.sources ?? []).some((s) => /Stories/i.test(s.label))} />
        </ul>
      ) : null}

      {history.length > 1 ? (
        <>
          <h5 className="so-class-title" style={{ marginTop: 18 }}>Historique des passes</h5>
          <ul className="vs-history">
            {history.slice(0, 12).map((h) => (
              <li key={h.id}>
                {/* Une passe passée est consultable : c'est là qu'on retrouve
                    quelle source était injoignable ce soir-là (§20). */}
                <button
                  type="button"
                  className={`vs-history__row${run?.id === h.id ? ' vs-history__row--current' : ''}`}
                  onClick={() => setRun(h)}
                  aria-label={`Voir la passe du ${quandFr(h.started_at)}`}
                >
                  <span>{quandFr(h.started_at)}</span>
                  <em className={`vs-history__st vs-history__st--${h.status.toLowerCase()}`}>{h.status}</em>
                  <span className="lc-hint">
                    {h.trigger === 'MANUAL' ? 'manuel' : 'planifié'} · {h.events_found} évén. ·{' '}
                    {h.signals_created + h.signals_updated} signal(aux) · {dureeFr(h.duration_ms)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Drawer>
  )
}
